import { serializeAppData } from "./app-data-envelope";
export { serializeAppData } from "./app-data-envelope";
import { emptyAppData, type AppData } from "../domain/types";
import { portableAppDataSchema } from "../domain/app-data-schema";
import { archiveAppsInTossOriginal, deleteAppsInTossData, getTossStorageState, mirrorAppsInTossData } from "./apps-in-toss";
import { APP_DATA_STORAGE_KEY, RECOVERY_KEY_PREFIX } from "./storage-keys";
export { RECOVERY_KEY_PREFIX } from "./storage-keys";

export type PortableAppData = Omit<Partial<AppData>, "version"> & { version?: 1 | 2 | 3 };

export function normalizeAppData(data: PortableAppData): AppData {
  if (!portableAppDataSchema.safeParse(data).success) throw new Error("준비표의 항목 형식을 읽지 못했어요. 기존 기록은 바꾸지 않았어요.");
  const fallback = structuredClone(emptyAppData);
  const projects = Array.isArray(data.projects) ? data.projects : fallback.projects;
  const activeProjectId = projects.some((project) => project.id === data.activeProjectId)
    ? data.activeProjectId
    : projects[0]?.id;
  return {
    ...fallback,
    ...data,
    version: 3,
    activeProjectId,
    projects,
    wardrobe: Array.isArray(data.wardrobe) ? data.wardrobe : fallback.wardrobe,
    marketDrafts: Array.isArray(data.marketDrafts) ? data.marketDrafts : fallback.marketDrafts,
    shootPlans: Array.isArray(data.shootPlans) ? data.shootPlans : fallback.shootPlans,
    recruitmentDrafts: Array.isArray(data.recruitmentDrafts) ? data.recruitmentDrafts : fallback.recruitmentDrafts,
    bookingDrafts: Array.isArray(data.bookingDrafts) ? data.bookingDrafts : fallback.bookingDrafts,
    safetyActions: Array.isArray(data.safetyActions) ? data.safetyActions : fallback.safetyActions,
    photos: Array.isArray(data.photos) ? data.photos : fallback.photos,
    challengeDrafts: Array.isArray(data.challengeDrafts) ? data.challengeDrafts : fallback.challengeDrafts,
    entitlement: data.entitlement ?? fallback.entitlement,
    analytics: data.analytics ?? fallback.analytics,
    lastPreferences: data.lastPreferences ?? fallback.lastPreferences,
  };
}

export type StorageIssue = { kind: "corrupt" | "unsupported_version" | "unavailable"; raw?: string };
export type AppDataRead = { data: AppData; issue?: StorageIssue };

function parseStoredData(raw: string): AppDataRead {
  const data = structuredClone(emptyAppData);
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return { data, issue: { kind: "corrupt", raw } };
    if (parsed.version !== 1 && parsed.version !== 2 && parsed.version !== 3) return { data, issue: { kind: "unsupported_version", raw } };
    if (!Array.isArray(parsed.projects) || parsed.projects.some((project: unknown) => !project || typeof project !== "object" || !Array.isArray((project as { tasks?: unknown }).tasks))) {
      return { data, issue: { kind: "corrupt", raw } };
    }
    return { data: normalizeAppData(parsed) };
  } catch {
    return { data, issue: { kind: "corrupt", raw } };
  }
}

export function readAppData(): AppDataRead {
  try {
    const raw = localStorage.getItem(APP_DATA_STORAGE_KEY);
    return raw === null ? { data: structuredClone(emptyAppData) } : parseStoredData(raw);
  } catch {
    return { data: structuredClone(emptyAppData), issue: { kind: "unavailable" } };
  }
}

export function loadAppData(): AppData { return readAppData().data; }

export function saveAppData(data: AppData) {
  if (getTossStorageState().status === "conflict") throw new Error("기기와 토스의 기록을 먼저 확인해 주세요.");
  // Recheck on every write: another tab or a newer app may have replaced the copy.
  if (readAppData().issue) throw new Error("기존 기록을 읽을 수 없어 덮어쓰지 않았어요. 원본을 확인해 주세요.");
  const serialized = serializeAppData(data);
  if (parseStoredData(serialized).issue) throw new Error("준비표 형식을 확인하지 못해 저장하지 않았어요.");
  localStorage.setItem(APP_DATA_STORAGE_KEY, serialized);
  if (localStorage.getItem(APP_DATA_STORAGE_KEY) !== serialized) throw new Error("기기에 저장됐는지 확인하지 못했어요.");
  void mirrorAppsInTossData(serialized);
}

export async function archiveAndResetAppData(expectedRaw: string): Promise<string> {
  if (localStorage.getItem(APP_DATA_STORAGE_KEY) !== expectedRaw) throw new Error("기록이 바뀌었어요. 다시 확인해 주세요.");
  await archiveAppsInTossOriginal(expectedRaw);
  if (localStorage.getItem(APP_DATA_STORAGE_KEY) !== expectedRaw) throw new Error("기록이 바뀌었어요. 다시 확인해 주세요.");
  const key = `${RECOVERY_KEY_PREFIX}${new Date().toISOString()}:${crypto.randomUUID()}`;
  localStorage.setItem(key, expectedRaw);
  if (localStorage.getItem(key) !== expectedRaw) throw new Error("원본 보관을 확인하지 못해 새로 시작하지 않았어요.");
  // A second read protects against replacing an intervening update while archiving.
  if (localStorage.getItem(APP_DATA_STORAGE_KEY) !== expectedRaw) throw new Error("기록이 바뀌었어요. 다시 확인해 주세요.");
  const serialized = serializeAppData(emptyAppData);
  localStorage.setItem(APP_DATA_STORAGE_KEY, serialized);
  if (localStorage.getItem(APP_DATA_STORAGE_KEY) !== serialized) throw new Error("새 준비표 저장을 확인하지 못했어요. 보관한 원본은 유지돼요.");
  void mirrorAppsInTossData(serialized);
  return key;
}

export function recoveryCopies(): Array<{ key: string; raw: string }> {
  const copies: Array<{ key: string; raw: string }> = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.startsWith(RECOVERY_KEY_PREFIX)) {
      const raw = localStorage.getItem(key);
      if (raw !== null) copies.push({ key, raw });
    }
  }
  return copies.sort((a, b) => b.key.localeCompare(a.key));
}

export function exportAppData(data: AppData): Blob {
  return new Blob([serializeAppData(data)], { type: "application/json" });
}

export async function deleteAppData() {
  await deleteAppsInTossData();
  for (const copy of recoveryCopies()) localStorage.removeItem(copy.key);
  localStorage.removeItem(APP_DATA_STORAGE_KEY);
}
